/**
 * BotsPage — Non-custodial bot launcher.
 *
 * History: this used to be a list view for the legacy MCN volume-keeper bot.
 * That architecture (custodial, daily orchestrator, activity_keeper_v1) was
 * removed for the haythix launch. The page is now a type chooser pointing to
 * each specialized bot's dedicated creator page.
 *
 * If a user still has a legacy bot record from before the pivot, a small
 * "Legacy bots to remove" section appears at the top with a delete-only
 * action so they can clean up. Once removed, the section disappears and only
 * the type chooser remains.
 */

import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bot, Grid3X3, TrendingDown, GitBranch, Zap, Radio,
  ShieldCheck, ChevronRight, Sparkles, Trash2, AlertTriangle,
  Activity, Layers,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getTierConfig } from '../constants/tiers';
import { useBots } from '../hooks/useData';
import { deleteBot } from '../services/api';
import { Spinner } from '../components/ui';

const BOT_TYPES = [
  {
    id: 'smart-trade',
    name: 'Smart Trade',
    icon: Activity,
    color: 'from-rose-400 to-pink-500',
    href: '/smart-trade',
    short: 'Single position with TP/SL automation.',
    desc: 'Open a position on a CEX, attach take-profits and stop-loss. haythix monitors the order book and exits at your levels. Works on spot for any pair on your connected exchange.',
    tier: null,
    badge: 'CORE',
  },
  {
    id: 'algo-orders',
    name: 'Algo Orders',
    icon: Layers,
    color: 'from-fuchsia-400 to-violet-500',
    href: '/algo-orders',
    short: 'TWAP, VWAP, Iceberg, Bracket execution.',
    desc: 'Institutional-grade order slicing. Split a large order across time (TWAP), match the daily volume profile (VWAP), hide your size (Iceberg), or wrap entry + TP + SL (Bracket).',
    tier: null,
    badge: 'POPULAR',
  },
  {
    id: 'grid',
    name: 'Grid Bot',
    icon: Grid3X3,
    color: 'from-emerald-400 to-teal-500',
    href: '/grid-bot',
    short: 'Buy low, sell high in a range.',
    desc: 'Places a ladder of buy and sell orders within a price range you set. Captures profit on every oscillation. Best for sideways markets.',
    tier: 'gridBot',
  },
  {
    id: 'dca-safety',
    name: 'DCA Safety Bot',
    icon: TrendingDown,
    color: 'from-blue-400 to-indigo-500',
    href: '/dca-safety',
    short: 'Average down on dips — with hard stops.',
    desc: 'Dollar-cost average into a position with safety nets: max drawdown, max additional buys, auto-pause on extreme moves. Knows when to stop.',
    tier: 'gridBot',
  },
  {
    id: 'conditional',
    name: 'Conditional Bot',
    icon: GitBranch,
    color: 'from-amber-400 to-orange-500',
    href: '/conditional-bots',
    short: 'IF this, THEN trade.',
    desc: 'Rule-based bots: "If BTC drops below $X, buy Y of ETH." Chain multiple conditions across pairs and indicators.',
    tier: 'conditionalBots',
  },
  {
    id: 'arbitrage',
    name: 'Arbitrage Bot',
    icon: Zap,
    color: 'from-violet-400 to-purple-500',
    href: '/arbitrage',
    short: 'Capture price gaps across exchanges.',
    desc: 'Watches the same pair across multiple connected exchanges. When the bid on one exceeds the ask on another by your threshold, fires both sides simultaneously.',
    tier: 'arbitrageDetector',
  },
  {
    id: 'webhook',
    name: 'TradingView Webhook',
    icon: Radio,
    color: 'from-cyan-400 to-blue-500',
    href: '/webhooks',
    short: 'Run your TradingView alerts as bots.',
    desc: 'Connect your TradingView Pine Script alerts. When the alert fires, haythix executes the trade on your connected exchange. No coding required.',
    tier: null,
  },
];

function LegacyBotRow({ bot, onDelete, busy }) {
  return (
    <div className="flex items-center gap-3 bg-dark-900/60 border border-amber-500/20 rounded-xl p-3">
      <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
        <Bot className="w-5 h-5 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-medium text-sm truncate">{bot.name || bot.botId}</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-dark-700 text-gray-400">{bot.pair}</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/20">
            {bot.strategyType || 'legacy'}
          </span>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">Legacy MCN volume-keeper record. Workers are already disabled; remove to clean up the list.</p>
      </div>
      <button
        onClick={() => onDelete(bot.botId)}
        disabled={busy === bot.botId}
        className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 disabled:opacity-40 flex items-center gap-1.5 flex-shrink-0"
      >
        {busy === bot.botId ? <Spinner size="sm" /> : <Trash2 className="w-3.5 h-3.5" />}
        Remove
      </button>
    </div>
  );
}

export default function BotsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tierCfg = getTierConfig(user?.tier);
  const { bots, loading, refresh } = useBots();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const legacyBots = bots || [];

  const handleDelete = useCallback(async (botId) => {
    setBusy(botId);
    setError(null);
    try {
      await deleteBot(botId);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  }, [refresh]);

  function hasFeature(featureKey) {
    if (!featureKey) return true;
    return tierCfg?.features?.[featureKey] === true;
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
          <Bot className="w-6 h-6" /> Trading Bots
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Pick a strategy type. Each runs on your own connected exchange or wallet — non-custodial, your keys stay with you.
        </p>
      </div>

      {/* Trust banner */}
      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-300">Non-custodial by design</p>
          <p className="text-xs text-emerald-300/80 mt-1">
            Bots authenticate using your exchange API key (CEX) or your connected wallet (DEX) — both configured in Settings → Connections.
            haythix never sees or stores your private keys. Revoke any bot's access by removing the underlying API key or wallet at any time.
          </p>
        </div>
      </div>

      {/* Legacy bots cleanup (only if any exist) */}
      {!loading && legacyBots.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-300">Legacy bot records to remove</p>
              <p className="text-xs text-amber-300/80 mt-1">
                These are leftover configs from the old volume-keeper architecture. The orchestrator and execution worker are <strong>already disabled</strong> — these records aren't running, but should be cleaned up.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {legacyBots.map(b => (
              <LegacyBotRow key={b.botId} bot={b} onDelete={handleDelete} busy={busy} />
            ))}
          </div>
          {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
        </div>
      )}

      {/* Bot type cards */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Available strategies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BOT_TYPES.map(type => {
            const accessible = hasFeature(type.tier);
            const Icon = type.icon;
            return (
              <Link
                key={type.id}
                to={accessible ? type.href : '/settings?tab=subscription'}
                className={`group bg-dark-800 rounded-2xl border border-dark-700/50 p-5 hover:border-dark-600 transition-all relative overflow-hidden ${accessible ? '' : 'opacity-70'}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${type.color} flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-white font-bold">{type.name}</h3>
                      {type.badge && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                          {type.badge}
                        </span>
                      )}
                      {!accessible && (
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/30 flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" /> UPGRADE
                        </span>
                      )}
                    </div>
                    <p className="text-emerald-400 text-xs font-medium">{type.short}</p>
                    <p className="text-gray-500 text-xs mt-2 leading-relaxed">{type.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-white flex-shrink-0 mt-2" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Spinner className="text-gray-500" />
        </div>
      )}
    </div>
  );
}
